import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDuration } from '@/lib/format';

type OriginalVideoDownloadButtonProps = {
  sourceUrl?: string | null;
  hasProAccess: boolean;
  sourceDuration?: number | null;
  clipDuration?: number | null;
  showExportHint?: boolean;
  appearance?: 'toolbar' | 'overlay';
  i18nPrefix?: string;
  onProRequired?: () => void;
};

export function OriginalVideoDownloadButton({
  sourceUrl,
  hasProAccess,
  sourceDuration,
  clipDuration,
  showExportHint = true,
  appearance = 'toolbar',
  i18nPrefix = 'projectEditor.originalVideoDownload',
  onProRequired,
}: OriginalVideoDownloadButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const canPromptForUpgrade = !hasProAccess && !!onProRequired;
  const disabled = !sourceUrl || (!hasProAccess && !onProRequired);
  const triggerLabel = t(`${i18nPrefix}.triggerLabel`);
  const isOverlay = appearance === 'overlay';

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!sourceUrl) return;
    if (canPromptForUpgrade) {
      onProRequired?.();
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={isOverlay ? 'relative z-20' : undefined}>
            {isOverlay ? (
              <Button
                type="button"
                variant="gradient"
                size="icon"
                className="h-8 w-8 md:h-7 md:w-7 flex-shrink-0"
                aria-label={triggerLabel}
                disabled={disabled}
                onClick={handleTriggerClick}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 sm:h-10 sm:w-10"
                aria-label={triggerLabel}
                disabled={disabled}
                onClick={handleTriggerClick}
              >
                <Download className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent align="center" side="top">
          {hasProAccess ? triggerLabel : t('projectEditor.proOnlyFeature')}
        </TooltipContent>
      </Tooltip>

      <OriginalVideoDownloadDialog
        open={open}
        onOpenChange={setOpen}
        sourceUrl={sourceUrl}
        sourceDuration={sourceDuration}
        clipDuration={clipDuration}
        showExportHint={showExportHint}
        i18nPrefix={i18nPrefix}
      />
    </>
  );
}

type OriginalVideoDownloadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceUrl?: string | null;
  sourceDuration?: number | null;
  clipDuration?: number | null;
  showExportHint?: boolean;
  i18nPrefix?: string;
};

/** The confirmation before downloading the original video, shared by the
 * editor button and the "⋯" menu of the /projects and /library cards. */
export function OriginalVideoDownloadDialog({
  open,
  onOpenChange,
  sourceUrl,
  sourceDuration,
  clipDuration,
  showExportHint = true,
  i18nPrefix = 'projectEditor.originalVideoDownload',
}: OriginalVideoDownloadDialogProps) {
  const { t } = useTranslation();
  const hasBothDurations = [sourceDuration, clipDuration].every(
    (duration) => typeof duration === 'number' && Number.isFinite(duration) && duration > 0,
  );

  const confirmDownload = () => {
    if (!sourceUrl) return;
    window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${i18nPrefix}.title`)}</DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">
              {hasBothDurations
                ? t(`${i18nPrefix}.description`, {
                    sourceDuration: formatDuration(sourceDuration),
                    clipDuration: formatDuration(clipDuration),
                  })
                : t(`${i18nPrefix}.descriptionWithoutDurations`)}
            </span>
            {showExportHint && (
              <span className="block">
                {t(`${i18nPrefix}.exportHint`)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t(`${i18nPrefix}.cancel`)}
          </Button>
          <Button type="button" onClick={confirmDownload}>
            {t(`${i18nPrefix}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
